import InnerBox from '@repo/ui/inner-box'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { useFormContext } from 'react-hook-form'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../lib/types/enums/staff-application/diploma.enum'
import {
  EducationLevelEnum,
  educationLevelEnumMapping
} from '../../../lib/types/enums/staff-application/education-level.enum'
import {
  NationalEducationStatusEnum,
  nationalEducationStatusEnumMapping
} from '../../../lib/types/enums/staff-application/national-education-status.enum'
import { StaffApplicationInput } from '../../../lib/types/models/staff-application/staff-application-input'
import {
  getSelectedEnumOption,
  getSelectedOption
} from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

type Props = {
  readOnly?: boolean
}

const StaffApplicationFormEducation: React.FC<Props> = ({ readOnly }) => {
  const form = useFormContext<StaffApplicationInput>()
  const { t } = useTranslate()

  return (
    <InnerBox title="Parcours éducatif">
      <div className="flex flex-col gap-3">
        <FormField
          control={form.control}
          name="levelOfEducation"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  readOnly={readOnly}
                  label="Niveau d'études"
                  options={Object.values(EducationLevelEnum).map((status) => ({
                    label: educationLevelEnumMapping[status],
                    value: status
                  }))}
                  selectedOption={
                    field.value
                      ? getSelectedOption(field.value, educationLevelEnumMapping[field.value])
                      : undefined
                  }
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="currentDiploma"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    readOnly={readOnly}
                    type="text"
                    label="Diplôme en cours"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="yearsOfExperience"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    readOnly={readOnly}
                    type="number"
                    isControllable
                    allowNegative={false}
                    label="Nombre d’années d’enseignement"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="diplomas"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <MultiSelectInput
                    {...field}
                    readOnly={readOnly}
                    label="Diplômes"
                    options={Object.values(DiplomaEnum).map((status) => ({
                      label: t(diplomaEnumMapping[status]),
                      value: status
                    }))}
                    variant="primary"
                    selectedOptions={
                      field.value?.map((value) => ({
                        label: t(diplomaEnumMapping[value]),
                        value
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="otherDiploma"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    readOnly={readOnly}
                    type="text"
                    label="Autres diplômes"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="memberOfNationalEducationStatus"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    readOnly={readOnly}
                    label="Membre de l’éducation national"
                    options={Object.values(NationalEducationStatusEnum).map((status) => ({
                      label: nationalEducationStatusEnumMapping[status],
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      nationalEducationStatusEnumMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="memberOfNationalEducationDate"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledDateStringInput {...field} readOnly={readOnly} label="Depuis" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </InnerBox>
  )
}

export default StaffApplicationFormEducation
